/**
 * Source Selector Component
 *
 * Dropdown to switch between multiple sources in a room.
 * Used in transcript and screen viewers.
 */

import { isScreenLikeSource, SourceType } from '@polygraf/shared'
import { Check, ChevronDown, FileText, Monitor } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { viewPathForService } from '../lib/view-path'

interface Source {
	id: string
	type: SourceType
	name: string
	format?: string | null
	isActive: boolean
	/** Set when a transcript has an active live web session — routes to /view/session. */
	liveDocumentId?: string | null
}

interface SourceSelectorProps {
	/** Currently selected source ID */
	currentSourceId: string
	/** List of available sources */
	sources: Source[]
	/** Room info for navigation context */
	room?: {
		id: string
		name: string
		spaceId: string
		spaceName: string
	}
	/** Filter to show only specific type */
	filterType?: SourceType
	/** Custom style for text color (for viewer integration) */
	textColor?: string
	/** Callback when source changes (optional, defaults to navigation) */
	onChange?: (sourceId: string) => void
}

export function SourceSelector({
	currentSourceId,
	sources,
	room,
	filterType,
	textColor = 'currentColor',
	onChange,
}: SourceSelectorProps) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const [isOpen, setIsOpen] = useState(false)
	const containerRef = useRef<HTMLDivElement>(null)

	// Filter sources by type if specified. Filtering for SCREEN must also match
	// legacy SCREENCAST rows — they render identically and MU's database still
	// has them; an exact match silently hides an existing classroom source from
	// the picker.
	const filteredSources = filterType
		? sources.filter((s) =>
				filterType === SourceType.SCREEN ? isScreenLikeSource(s.type) : s.type === filterType,
			)
		: sources

	// Nothing to switch between → render nothing. Computed here but RETURNED
	// BELOW, after every hook. This used to be an early `return null` above the
	// two useEffects, which is a rules-of-hooks violation (Biome flags it as
	// correctness/useHookAtTopLevel): any render that took it would run fewer
	// hooks than the previous one and React would throw.
	//
	// No caller can reach that today — both viewers duplicate this exact
	// predicate before rendering the element, so a shrinking sibling list
	// unmounts the component instead of re-rendering it, and unmounting runs
	// cleanups normally. The violation is therefore latent, not a live crash;
	// it is fixed so the component is correct for any caller, including one
	// that (reasonably) lets it decide its own visibility.
	const isVisible = filteredSources.length > 1

	const currentSource = filteredSources.find((s) => s.id === currentSourceId)

	// Only matters for a caller that keeps the component mounted while it turns
	// invisible: without this the dropdown would reappear already open when a
	// sibling source comes back. Dead weight under today's two callers.
	useEffect(() => {
		if (!isVisible) setIsOpen(false)
	}, [isVisible])

	// Close on outside click
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setIsOpen(false)
			}
		}

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside)
			return () => document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [isOpen])

	// Close on escape
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				setIsOpen(false)
			}
		}

		if (isOpen) {
			document.addEventListener('keydown', handleKeyDown)
			return () => document.removeEventListener('keydown', handleKeyDown)
		}
	}, [isOpen])

	const handleSelectSource = (source: Source) => {
		setIsOpen(false)

		if (source.id === currentSourceId) return

		if (onChange) {
			onChange(source.id)
		} else {
			// Single owner of the transcript/screen/session routing decision —
			// previously inlined here without the live-session branch, so a
			// transcript with an active web session wrongly routed to the
			// transcript-only viewer (missing the video).
			navigate(viewPathForService(source))
		}
	}

	const SourceIcon = ({ type }: { type: SourceType }) => {
		if (type === SourceType.TRANSCRIPT) {
			return <FileText size={14} className="text-primary" />
		}
		return <Monitor size={14} className="text-secondary" />
	}

	// The "only one source" bail-out, moved here from above the hooks so every
	// hook runs on every render.
	if (!isVisible) {
		return null
	}

	return (
		<div ref={containerRef} className="relative">
			<button
				className="flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors hover:bg-black/5"
				style={{
					color: textColor,
					borderColor: `${textColor}30`,
				}}
				onClick={() => setIsOpen(!isOpen)}
				aria-expanded={isOpen}
				aria-haspopup="listbox"
				aria-label={t('sourceSelector.selectSource')}
			>
				{currentSource && <SourceIcon type={currentSource.type} />}
				<span className="text-sm font-medium max-w-[150px] truncate">
					{currentSource?.name ?? t('sourceSelector.selectSource')}
				</span>
				<ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
			</button>

			{isOpen && (
				<div
					className="absolute top-full left-0 mt-1 min-w-[200px] max-w-[300px] bg-base-100 rounded-lg shadow-lg border border-base-300 z-50 overflow-hidden"
					role="listbox"
				>
					{room && (
						<div className="px-3 py-2 border-b border-base-300 bg-base-200/50">
							<p className="text-xs text-base-content/60 truncate">{room.spaceName}</p>
							<p className="text-sm font-medium truncate">{room.name}</p>
						</div>
					)}

					<div className="py-1 max-h-[300px] overflow-y-auto">
						{filteredSources.map((source) => {
							const isSelected = source.id === currentSourceId

							return (
								<button
									key={source.id}
									className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
										isSelected
											? 'bg-primary/10 text-primary'
											: 'hover:bg-base-200 text-base-content'
									}`}
									onClick={() => handleSelectSource(source)}
									role="option"
									aria-selected={isSelected}
								>
									<SourceIcon type={source.type} />
									<div className="flex-1 min-w-0">
										<p className="text-sm font-medium truncate">{source.name}</p>
										{source.format && (
											<p className="text-xs text-base-content/60">{source.format}</p>
										)}
									</div>
									{isSelected && <Check size={16} className="text-primary flex-shrink-0" />}
									{!source.isActive && (
										<span className="text-xs px-1.5 py-0.5 rounded bg-base-300 text-base-content/60">
											{t('sourceSelector.inactive')}
										</span>
									)}
								</button>
							)
						})}
					</div>

					{/* Show link to room if we have room info */}
					{room && (
						<div className="border-t border-base-300 px-3 py-2">
							<button
								className="text-xs text-primary hover:underline"
								onClick={() => navigate(`/spaces/${room.spaceId}/rooms/${room.id}`)}
							>
								{t('sourceSelector.showAllServices')}
							</button>
						</div>
					)}
				</div>
			)}
		</div>
	)
}
